import type { Maybe, Undef } from 'types/UtilityTypes';
import { QuotingPortals } from 'types/enums/Integrations';

import { QuoteMode } from '../types';

/**
 * Central policy for server-side submit-bid support by portal and quote mode.
 *
 * How it works:
 * - Key: bidding portal integration name.
 * - Value: quote modes that portal currently supports for server submit.
 *
 * Why this exists:
 * - Keeps rollout logic out of UI components/hooks.
 * - Avoids scattered one-off conditionals as support expands.
 *
 * To enable new access after shipping:
 * - Add the portal and/or mode here (for example, add `QuoteMode.LTL` for a portal).
 */
const SERVER_SUBMIT_PORTAL_SUPPORT_BY_MODE: Partial<
  Record<QuotingPortals, ReadonlyArray<QuoteMode>>
> = {
  [QuotingPortals.CarrierPoint]: [QuoteMode.TL],
  [QuotingPortals.InsightTMS]: [QuoteMode.TL],
  [QuotingPortals.TruckerTools]: [QuoteMode.TL],
  [QuotingPortals.Manhattan]: [QuoteMode.TL],
  [QuotingPortals.MercuryGate]: [QuoteMode.TL],
  [QuotingPortals.Kewill]: [QuoteMode.TL],
  [QuotingPortals.EmergeMarket]: [QuoteMode.TL],
  [QuotingPortals.NavisphereCarrier]: [QuoteMode.TL],
  [QuotingPortals.OTM]: [QuoteMode.TL],
  [QuotingPortals.UberFreight]: [QuoteMode.TL],
};

export function getServerSubmitPortalNamesForQuoteMode(
  quoteMode: QuoteMode
): QuotingPortals[] {
  return Object.entries(SERVER_SUBMIT_PORTAL_SUPPORT_BY_MODE)
    .filter(([, supportedModes]) => supportedModes?.includes(quoteMode))
    .map(([portal]) => portal as QuotingPortals);
}

export function isServerSubmitPortalEnabledForQuoteMode(
  portalName: string,
  quoteMode: QuoteMode
): portalName is QuotingPortals {
  return getServerSubmitPortalNamesForQuoteMode(quoteMode).includes(
    portalName as QuotingPortals
  );
}

// Prefer the email-detected bidding portal when it supports server submit for
// the current quote mode. Fall back to the first supported portal when detection
// is missing or not yet enabled for server submit (attribution-only portals).
export function resolveDefaultServerSubmitPortalName(
  supportedPortalNames: ReadonlyArray<QuotingPortals>,
  detectedBiddingPortal: Maybe<Undef<string>>
): Maybe<QuotingPortals> {
  if (supportedPortalNames.length === 0) {
    return null;
  }

  if (detectedBiddingPortal?.trim()) {
    if (
      supportedPortalNames.includes(detectedBiddingPortal as QuotingPortals)
    ) {
      return detectedBiddingPortal as QuotingPortals;
    }
  }

  return supportedPortalNames[0] ?? null;
}
